/**
 * SessionTimeout
 *
 * Enforces (1) an inactivity timeout and (2) an absolute session lifetime cap.
 * Mounted once for authenticated users. Timeout values come from the backend
 * (/auth/session-config) so they are tunable without a frontend deploy.
 *
 * IMPORTANT (per requirements): the countdown is NOT reset by passive activity
 * (mouse move, scroll, keystrokes). It only resets when the user explicitly
 * clicks "Continue Session" in the timeout prompt. If the prompt's countdown
 * reaches zero with no click, the user is logged out automatically.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/utils/auth';
import apiClient from '@/utils/api';

const decodeAbsExp = () => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.abs_exp ? payload.abs_exp * 1000 : null; // ms
  } catch {
    return null;
  }
};

const SessionTimeout = () => {
  const { user, logout } = useAuth();
  const [config, setConfig] = useState(null); // {idle_seconds, warning_seconds, absolute_seconds}
  const [warningOpen, setWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const resetRef = useRef(Date.now());          // last explicit "Continue" (or login)
  const absoluteDeadlineRef = useRef(null);
  const loggingOutRef = useRef(false);

  useEffect(() => {
    if (!user) { setConfig(null); return; }
    let active = true;
    apiClient.get('/auth/session-config')
      .then((res) => { if (active) setConfig(res.data); })
      .catch(() => {});
    absoluteDeadlineRef.current = decodeAbsExp();
    resetRef.current = Date.now();
    return () => { active = false; };
  }, [user]);

  const doTimeoutLogout = useCallback(async (reason) => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      await apiClient.post('/auth/session-timeout-log', { reason }).catch(() => {});
      await logout();
    } finally {
      window.location.href = `/login?reason=${reason}`;
    }
  }, [logout]);

  // Timer loop — driven purely by elapsed time since the last explicit reset,
  // NOT by user activity.
  useEffect(() => {
    if (!user || !config) return undefined;
    const interval = setInterval(() => {
      const now = Date.now();

      // Absolute hard cap
      if (absoluteDeadlineRef.current && now >= absoluteDeadlineRef.current) {
        doTimeoutLogout('absolute');
        return;
      }

      const idleMs = config.idle_seconds * 1000;
      const warnMs = config.warning_seconds * 1000;
      const elapsed = now - resetRef.current;

      if (elapsed >= idleMs) {
        // Prompt expired with no explicit click -> log out
        doTimeoutLogout('idle');
      } else if (elapsed >= idleMs - warnMs) {
        setWarningOpen(true);
        setCountdown(Math.max(0, Math.ceil((idleMs - elapsed) / 1000)));
      } else if (warningOpen) {
        setWarningOpen(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [user, config, warningOpen, doTimeoutLogout]);

  const continueSession = async () => {
    resetRef.current = Date.now();
    setWarningOpen(false);
    try {
      const res = await apiClient.post('/auth/refresh');
      if (res.data?.token) localStorage.setItem('jwt_token', res.data.token);
    } catch {
      doTimeoutLogout('absolute');
    }
  };

  if (!user || !config) return null;

  return (
    <Dialog open={warningOpen} onOpenChange={() => { /* control-only; no outside dismiss */ }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="session-timeout-warning"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-500" />
            Session about to expire
          </DialogTitle>
          <DialogDescription>
            Your session will expire in{' '}
            <span className="font-semibold text-foreground" data-testid="session-countdown">{countdown}</span>{' '}
            seconds due to inactivity. Click “Continue Session” to stay logged in.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => doTimeoutLogout('manual')} data-testid="session-logout-now-btn">
            Log Out
          </Button>
          <Button onClick={continueSession} data-testid="session-stay-logged-in-btn">
            Continue Session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTimeout;
